import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmartTemplateField } from '../types';
import { FieldAssignment, normalizeAssignments } from '../utils/smartFieldMarkers';
import {
    ActiveResizeState,
    ActiveMoveState,
    LineMetric,
    getVisualLineDescriptor,
    getFieldTint,
    getAssignmentPreview,
    isSameAssignment,
    isSameResizeState,
    isSameMoveState,
    isStructuralBlankLine,
    resizeAssignmentRange,
    moveAssignmentRange,
} from '../utils/smartFieldOverlayUtils';

interface SmartFieldOverlayCanvasProps {
    content: string;
    assignments: FieldAssignment[];
    smartFields?: SmartTemplateField[];
    onAssignmentsChange: (assignments: FieldAssignment[]) => void;
    minHeight?: string;
    chromeless?: boolean;
    renderLineContent?: boolean;
    transparentBackground?: boolean;
}

const SmartFieldOverlayCanvas: React.FC<SmartFieldOverlayCanvasProps> = ({
    content,
    assignments,
    smartFields = [],
    onAssignmentsChange,
    minHeight = '450px',
    chromeless = false,
    renderLineContent = true,
    transparentBackground = false,
}) => {
    const { t } = useTranslation();
    const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
    const [activeResize, setActiveResize] = useState<ActiveResizeState | null>(null);
    const [activeMove, setActiveMove] = useState<ActiveMoveState | null>(null);
    const [lineMetrics, setLineMetrics] = useState<LineMetric[]>([]);
    const [activeResizePreviewY, setActiveResizePreviewY] = useState<number | null>(null);
    const [isSelectionMenuOpen, setIsSelectionMenuOpen] = useState(false);
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const lineRefs = useRef<Array<HTMLDivElement | null>>([]);
    const assignmentsRef = useRef<FieldAssignment[]>(assignments);
    const activeResizeRef = useRef<ActiveResizeState | null>(null);
    const activeMoveRef = useRef<ActiveMoveState | null>(null);
    const selectionMenuRef = useRef<HTMLDivElement | null>(null);

    const lines = useMemo(() => {
        if (!content.length) {
            return [''];
        }

        return content.split('\n');
    }, [content]);

    const safeAssignments = useMemo(
        () => normalizeAssignments(assignments, lines.length),
        [assignments, lines.length]
    );

    useEffect(() => {
        assignmentsRef.current = safeAssignments;
    }, [safeAssignments]);

    useEffect(() => {
        const currentSelection = selection;
        if (!currentSelection) {
            return;
        }

        const stillExists = safeAssignments.some((assignment) => (
            assignment.startLine === currentSelection.start &&
            assignment.endLine === currentSelection.end
        ));

        if (!stillExists) {
            setSelection(null);
        }
    }, [safeAssignments, selection]);

    useEffect(() => {
        if (!isSelectionMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (selectionMenuRef.current && target && !selectionMenuRef.current.contains(target)) {
                setIsSelectionMenuOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsSelectionMenuOpen(false);
            }
        };

        window.addEventListener('mousedown', handlePointerDown);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('mousedown', handlePointerDown);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSelectionMenuOpen]);

    useEffect(() => {
        lineRefs.current = lineRefs.current.slice(0, lines.length);
    }, [lines.length]);

    useLayoutEffect(() => {
        const nextMetrics = lineRefs.current
            .slice(0, lines.length)
            .map((lineNode) => {
                if (!lineNode) {
                    return null;
                }

                const top = lineNode.offsetTop;
                const height = lineNode.offsetHeight;

                return {
                    top,
                    bottom: top + height,
                    height,
                };
            })
            .filter((metric): metric is LineMetric => metric !== null);

        setLineMetrics(nextMetrics);
    }, [lines.length, safeAssignments, selection]);

    useEffect(() => {
        activeResizeRef.current = activeResize;
    }, [activeResize]);

    useEffect(() => {
        activeMoveRef.current = activeMove;
    }, [activeMove]);

    const assignmentByLine = useMemo(() => {
        const map = new Map<number, FieldAssignment>();
        safeAssignments.forEach((assignment) => {
            for (let lineIndex = assignment.startLine; lineIndex <= assignment.endLine; lineIndex += 1) {
                map.set(lineIndex, assignment);
            }
        });
        return map;
    }, [safeAssignments]);

    const selectedLineIndices = useMemo(() => {
        if (!selection) {
            return [];
        }

        const start = Math.min(selection.start, selection.end);
        const end = Math.max(selection.start, selection.end);
        return Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
    }, [selection]);

    const selectedAssignment = useMemo(() => {
        if (selectedLineIndices.length === 0) {
            return null;
        }

        const first = assignmentByLine.get(selectedLineIndices[0]);
        if (!first) {
            return null;
        }

        const allMatch = selectedLineIndices.every((lineIndex) => {
            const candidate = assignmentByLine.get(lineIndex);
            return candidate && candidate.code === first.code && candidate.startLine === first.startLine && candidate.endLine === first.endLine;
        });

        return allMatch ? first : null;
    }, [assignmentByLine, selectedLineIndices]);

    const activeFieldLookup = useMemo(() => {
        const lookup = new Map<string, SmartTemplateField>();
        smartFields.forEach((field) => {
            lookup.set(field.code, field);
        });
        return lookup;
    }, [smartFields]);

    const assignmentSummaries = useMemo(() => safeAssignments.map((assignment) => {
        const field = activeFieldLookup.get(assignment.code);
        return {
            ...assignment,
            title: field?.label || assignment.code,
            preview: getAssignmentPreview(lines, assignment),
            lineLabel: assignment.startLine === assignment.endLine
                ? t('smartFieldOverlay.lineLabelSingle', { line: assignment.startLine + 1 })
                : t('smartFieldOverlay.lineLabelRange', { start: assignment.startLine + 1, end: assignment.endLine + 1 }),
        };
    }), [activeFieldLookup, lines, safeAssignments, t]);

    const totalSelections = assignmentSummaries.length;

    const clearSelection = useCallback(() => {
        activeResizeRef.current = null;
        activeMoveRef.current = null;
        setSelection(null);
        setActiveResize(null);
        setActiveMove(null);
        setActiveResizePreviewY(null);
        setIsSelectionMenuOpen(false);
    }, []);

    const setLineRef = useCallback((lineIndex: number, node: HTMLDivElement | null) => {
        lineRefs.current[lineIndex] = node;
    }, []);

    const focusAssignment = useCallback((assignment: FieldAssignment) => {
        clearSelection();
        setSelection({
            start: assignment.startLine,
            end: assignment.endLine,
        });

        const lineNode = lineRefs.current[assignment.startLine];
        const canvas = canvasRef.current;

        if (lineNode && canvas) {
            const targetTop = Math.max(lineNode.offsetTop - 48, 0);
            canvas.scrollTo({
                top: targetTop,
                behavior: 'smooth',
            });
        }
    }, [clearSelection]);

    const getCanvasPointerY = useCallback((clientY: number) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return null;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const relativeY = clientY - canvasRect.top + canvas.scrollTop;
        const maxY = Math.max(canvas.scrollHeight, canvas.clientHeight);
        return Math.max(0, Math.min(relativeY, maxY));
    }, []);

    const getLineIndexFromPointer = useCallback((clientX: number, clientY: number) => {
        if (lines.length === 0) {
            return null;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            return null;
        }

        const canvasRect = canvas.getBoundingClientRect();
        if (clientY <= canvasRect.top) {
            return 0;
        }

        if (clientY >= canvasRect.bottom) {
            return lines.length - 1;
        }

        const hitTarget = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        const lineElement = hitTarget?.closest?.('[data-line-index]') as HTMLElement | null;
        const rawLineIndex = lineElement?.dataset.lineIndex;

        if (rawLineIndex) {
            const parsedLineIndex = Number(rawLineIndex);
            if (!Number.isNaN(parsedLineIndex)) {
                return parsedLineIndex;
            }
        }

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        lineRefs.current.forEach((lineNode, index) => {
            if (!lineNode) {
                return;
            }

            const lineRect = lineNode.getBoundingClientRect();
            const midpoint = lineRect.top + (lineRect.height / 2);
            const distance = Math.abs(midpoint - clientY);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });

        return Number.isFinite(closestDistance) ? closestIndex : null;
    }, [lines.length]);

    const getResizeLineIndex = useCallback((pointerY: number, edge: 'start' | 'end') => {
        if (lineMetrics.length === 0) {
            return lines.length > 0 ? 0 : null;
        }

        if (edge === 'start') {
            for (let index = lineMetrics.length - 1; index >= 0; index -= 1) {
                if (pointerY >= lineMetrics[index].top) {
                    return index;
                }
            }

            return 0;
        }

        for (let index = 0; index < lineMetrics.length; index += 1) {
            if (pointerY <= lineMetrics[index].bottom) {
                return index;
            }
        }

        return lineMetrics.length - 1;
    }, [lineMetrics, lines.length]);

    const commitAssignments = useCallback((nextAssignments: FieldAssignment[]) => {
        assignmentsRef.current = nextAssignments;
        onAssignmentsChange(nextAssignments);
    }, [onAssignmentsChange]);

    const handleLineMouseDown = useCallback((lineIndex: number) => {
        const assignedSelection = assignmentByLine.get(lineIndex);
        if (!assignedSelection) {
            clearSelection();
            return;
        }

        setIsSelectionMenuOpen(false);
        activeResizeRef.current = null;
        activeMoveRef.current = {
            code: assignedSelection.code,
            startLine: assignedSelection.startLine,
            endLine: assignedSelection.endLine,
            pointerOffset: lineIndex - assignedSelection.startLine,
        };
        setSelection({
            start: assignedSelection.startLine,
            end: assignedSelection.endLine,
        });
        setActiveResize(null);
        setActiveMove({
            code: assignedSelection.code,
            startLine: assignedSelection.startLine,
            endLine: assignedSelection.endLine,
            pointerOffset: lineIndex - assignedSelection.startLine,
        });
        setActiveResizePreviewY(null);
    }, [assignmentByLine, clearSelection]);

    const handleResizeMouseDown = useCallback((assignment: FieldAssignment, edge: 'start' | 'end') => {
        const nextResize = {
            code: assignment.code,
            startLine: assignment.startLine,
            endLine: assignment.endLine,
            edge,
        };
        const lineMetric = lineMetrics[edge === 'start' ? assignment.startLine : assignment.endLine];
        const boundaryY = edge === 'start'
            ? lineMetric?.top ?? 0
            : lineMetric?.bottom ?? 0;

        activeResizeRef.current = nextResize;
        activeMoveRef.current = null;
        setSelection({
            start: assignment.startLine,
            end: assignment.endLine,
        });
        setActiveResize(nextResize);
        setActiveMove(null);
        setActiveResizePreviewY(boundaryY);
        setIsSelectionMenuOpen(false);
    }, [lineMetrics]);

    useEffect(() => {
        if (!activeResize && !activeMove) {
            return undefined;
        }

        const handleMouseMove = (event: MouseEvent) => {
            const currentResize = activeResizeRef.current;
            if (currentResize) {
                const pointerY = getCanvasPointerY(event.clientY);
                if (pointerY === null) {
                    return;
                }

                setActiveResizePreviewY(pointerY);
                const lineIndex = getResizeLineIndex(pointerY, currentResize.edge);
                if (lineIndex === null) {
                    return;
                }

                const { nextAssignments, nextActiveResize } = resizeAssignmentRange(
                    assignmentsRef.current,
                    currentResize,
                    currentResize.edge,
                    lineIndex,
                    lines.length
                );

                if (!nextActiveResize || isSameResizeState(currentResize, nextActiveResize)) {
                    return;
                }

                commitAssignments(nextAssignments);
                activeResizeRef.current = nextActiveResize;
                setActiveResize(nextActiveResize);
                setSelection({
                    start: nextActiveResize.startLine,
                    end: nextActiveResize.endLine,
                });
                return;
            }

            const currentMove = activeMoveRef.current;
            if (!currentMove) {
                return;
            }

            const lineIndex = getLineIndexFromPointer(event.clientX, event.clientY);
            if (lineIndex === null) {
                return;
            }

            const { nextAssignments, nextActiveMove } = moveAssignmentRange(
                assignmentsRef.current,
                currentMove,
                lineIndex,
                currentMove.pointerOffset,
                lines.length
            );

            if (!nextActiveMove || isSameMoveState(currentMove, nextActiveMove)) {
                return;
            }

            commitAssignments(nextAssignments);
            activeMoveRef.current = nextActiveMove;
            setActiveMove(nextActiveMove);
            setSelection({
                start: nextActiveMove.startLine,
                end: nextActiveMove.endLine,
            });
        };

        const stopDragging = () => {
            activeResizeRef.current = null;
            activeMoveRef.current = null;
            setActiveResize(null);
            setActiveMove(null);
            setActiveResizePreviewY(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopDragging);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopDragging);
        };
    }, [activeMove, activeResize, commitAssignments, getCanvasPointerY, getLineIndexFromPointer, getResizeLineIndex, lines.length]);

    if (safeAssignments.length === 0 && !chromeless) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                {t('smartFieldOverlay.empty')}
            </div>
        );
    }

    const canvas = (
        <div
            ref={canvasRef}
            className={`relative overflow-auto select-none ${chromeless ? 'px-0 py-0' : 'px-5 py-4'} ${transparentBackground ? 'bg-transparent' : 'bg-white'}`}
            style={{ minHeight }}
        >
            {lines.map((line, index) => {
                const lineAssignment = assignmentByLine.get(index);
                const isSelected = selectedLineIndices.includes(index);
                const lineDescriptor = getVisualLineDescriptor(line);
                // Blanco regels rondom koppen of tussen lijstitems zijn puur
                // structurele markdown-opmaak; klap ze in zodat de overlay
                // overeenkomt met de gerenderde weergave.
                const isStructuralBlank = isStructuralBlankLine(lines, index);
                const field = lineAssignment ? activeFieldLookup.get(lineAssignment.code) : undefined;
                const selectionStart = selection ? Math.min(selection.start, selection.end) : -1;
                const isSelectionStart = isSelected && index === selectionStart;
                const previousSelection = selectedLineIndices.includes(index - 1);
                const nextSelection = selectedLineIndices.includes(index + 1);
                const sameAssignedPrevious = Boolean(
                    lineAssignment &&
                    assignmentByLine.get(index - 1) &&
                    assignmentByLine.get(index - 1)?.code === lineAssignment.code &&
                    assignmentByLine.get(index - 1)?.startLine === lineAssignment.startLine &&
                    assignmentByLine.get(index - 1)?.endLine === lineAssignment.endLine
                );
                const sameAssignedNext = Boolean(
                    lineAssignment &&
                    assignmentByLine.get(index + 1) &&
                    assignmentByLine.get(index + 1)?.code === lineAssignment.code &&
                    assignmentByLine.get(index + 1)?.startLine === lineAssignment.startLine &&
                    assignmentByLine.get(index + 1)?.endLine === lineAssignment.endLine
                );
                const sectionTint = lineAssignment ? getFieldTint(lineAssignment.code) : null;
                const tint = isSelected
                    ? lineAssignment
                        ? `${sectionTint?.active || ''} text-gray-900`
                        : 'border-primary-300 bg-primary-50/70 text-gray-900'
                    : lineAssignment
                        ? sectionTint?.base || ''
                        : 'border-transparent bg-transparent text-gray-700';
                const showAssignedLabel = Boolean(lineAssignment && lineAssignment.startLine === index);
                const showTopResizeHandle = Boolean(lineAssignment && lineAssignment.startLine === index);
                const showBottomResizeHandle = Boolean(lineAssignment && lineAssignment.endLine === index);
                const isTopResizeActive = Boolean(
                    activeResize &&
                    lineAssignment &&
                    isSameAssignment(activeResize, lineAssignment) &&
                    activeResize.edge === 'start'
                );
                const isBottomResizeActive = Boolean(
                    activeResize &&
                    lineAssignment &&
                    isSameAssignment(activeResize, lineAssignment) &&
                    activeResize.edge === 'end'
                );
                const connectPrevious = isSelected ? previousSelection : sameAssignedPrevious;
                const connectNext = isSelected ? nextSelection : sameAssignedNext;
                const isMultiLineSelection = isSelected && (previousSelection || nextSelection);
                const lineChrome = isSelected || lineAssignment;
                const headingLevel = lineDescriptor.kind === 'heading' ? lineDescriptor.level : 0;
                const listIndent = lineDescriptor.kind === 'unordered-list' || lineDescriptor.kind === 'ordered-list'
                    ? lineDescriptor.indent
                    : 0;
                const orderedListMarker = lineDescriptor.kind === 'ordered-list' ? lineDescriptor.marker : '';
                const contentClassName = lineDescriptor.kind === 'heading'
                    ? headingLevel === 1
                        ? 'px-[2px] py-[3px] text-[24px] font-bold leading-[1.2] text-[rgb(55,53,47)]'
                        : headingLevel === 2
                            ? 'px-[2px] py-[3px] text-[20px] font-semibold leading-[1.3] text-[rgb(55,53,47)]'
                            : headingLevel === 3
                                ? 'px-[2px] py-[3px] text-[18px] font-semibold leading-[1.3] text-[rgb(55,53,47)]'
                                : 'px-[2px] py-[3px] text-[17px] font-semibold leading-[1.4] text-[rgb(55,53,47)]'
                    : lineDescriptor.kind === 'quote'
                        ? 'px-4 py-[3px] text-[16px] leading-[1.5] text-[rgba(55,53,47,0.6)]'
                        : lineDescriptor.kind === 'blank'
                            ? isStructuralBlank
                                ? 'h-0 overflow-hidden px-0 py-0'
                                : 'min-h-[6px] px-0 py-0 text-[rgb(55,53,47)]'
                            : 'px-[2px] py-[3px] text-[16px] leading-[1.5] text-[rgb(55,53,47)]';

                return (
                    <div
                        key={`line-${index}`}
                        ref={(node) => setLineRef(index, node)}
                        data-line-index={index}
                        onMouseDown={(event) => {
                            if (event.button !== 0) {
                                return;
                            }
                            event.preventDefault();
                            handleLineMouseDown(index);
                        }}
                        className={`group relative w-full text-left ${isSelected ? 'z-[2]' : showTopResizeHandle || showBottomResizeHandle ? 'z-[1]' : ''}`}
                    >
                        <div
                            className={`pointer-events-none absolute inset-0 transition-colors ${lineChrome
                                ? `${tint} border-x-2`
                                : 'group-hover:bg-gray-50/70'
                                } ${isSelected && !isMultiLineSelection ? `ring-2 ring-inset ${lineAssignment ? sectionTint?.activeRing || '' : 'ring-primary-200/80'}` : ''} ${lineChrome && !connectPrevious ? 'rounded-t-[14px] border-t-2' : ''} ${lineChrome && !connectNext ? 'rounded-b-[14px] border-b-2' : ''} ${lineChrome && connectPrevious ? 'border-t-0' : ''} ${lineChrome && connectNext ? 'border-b-0' : ''} ${isSelected && previousSelection ? 'border-t-0' : ''} ${isSelected && nextSelection ? 'border-b-0' : ''}`}
                        ></div>

                        {isSelected && isMultiLineSelection && (
                            <div
                                className={`pointer-events-none absolute inset-0 ${!connectPrevious ? 'rounded-t-[14px]' : ''} ${!connectNext ? 'rounded-b-[14px]' : ''}`}
                                style={{
                                    boxShadow: [
                                        !connectPrevious ? `inset 0 2px 0 0 ${lineAssignment ? sectionTint?.activeEdge || 'rgba(191, 219, 254, 0.95)' : 'rgba(191, 219, 254, 0.95)'}` : '',
                                        !connectNext ? `inset 0 -2px 0 0 ${lineAssignment ? sectionTint?.activeEdge || 'rgba(191, 219, 254, 0.95)' : 'rgba(191, 219, 254, 0.95)'}` : '',
                                    ].filter(Boolean).join(', '),
                                }}
                            ></div>
                        )}

                        {showTopResizeHandle && lineAssignment && !isTopResizeActive && (
                            <button
                                type="button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleResizeMouseDown(lineAssignment, 'start');
                                }}
                                className="absolute -top-[10px] left-5 right-5 z-20 flex h-5 cursor-row-resize items-center justify-center"
                                title={t('smartFieldOverlay.resizeTopTitle')}
                            >
                                <span className={`h-1.5 w-full rounded-full border shadow-sm ${sectionTint?.base || ''}`}></span>
                            </button>
                        )}

                        {showAssignedLabel && field && (
                            <div className="-top-3 absolute right-5 z-30 flex flex-col items-end gap-2">
                                <span className={`inline-flex items-center rounded-full border bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700 shadow-sm backdrop-blur ${sectionTint?.base || ''}`}>
                                    {field.label || field.code}
                                </span>
                            </div>
                        )}

                        {showAssignedLabel && !field && lineAssignment && (
                            <div className="-top-3 absolute right-5 z-30 flex flex-col items-end gap-2">
                                <span className={`inline-flex items-center rounded-full border bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700 shadow-sm backdrop-blur ${sectionTint?.base || ''}`}>
                                    {lineAssignment.code}
                                </span>
                            </div>
                        )}

                        {isSelectionStart && selectedAssignment && !chromeless && (
                            <div className="-top-3 absolute left-5 z-30">
                                <span className="inline-flex items-center rounded-full border border-white/80 bg-white/90 px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
                                    {t('smartFieldOverlay.lineLabelRange', { start: selectedAssignment.startLine + 1, end: selectedAssignment.endLine + 1 })}
                                </span>
                            </div>
                        )}

                        <div
                            className={`relative z-10 min-w-0 ${contentClassName} ${lineDescriptor.kind === 'quote' ? 'border-l-[3px] border-[rgba(55,53,47,0.18)] bg-[rgba(241,241,239,0.6)] rounded-r-[3px]' : ''} ${renderLineContent ? '' : 'opacity-0'}`}
                            style={lineDescriptor.kind === 'unordered-list' || lineDescriptor.kind === 'ordered-list'
                                ? { paddingLeft: `${24 + (listIndent * 24)}px` }
                                : undefined}
                        >
                            {lineDescriptor.kind === 'unordered-list' && (
                                <span className="mr-2 inline-block w-4 text-center text-[16px] leading-[1.5] text-[rgb(55,53,47)]">
                                    •
                                </span>
                            )}
                            {lineDescriptor.kind === 'ordered-list' && (
                                <span className="mr-2 inline-block min-w-[1.5rem] text-right text-[16px] leading-[1.5] text-[rgb(55,53,47)]">
                                    {orderedListMarker}
                                </span>
                            )}
                            {lineDescriptor.kind === 'blank' ? (
                                <span className="text-transparent">spacer</span>
                            ) : (
                                <span key={lineDescriptor.html} dangerouslySetInnerHTML={{ __html: lineDescriptor.html }} />
                            )}
                        </div>

                        {showBottomResizeHandle && lineAssignment && !isBottomResizeActive && (
                            <button
                                type="button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleResizeMouseDown(lineAssignment, 'end');
                                }}
                                className="absolute -bottom-[10px] left-5 right-5 z-20 flex h-5 cursor-row-resize items-center justify-center"
                                title={t('smartFieldOverlay.resizeBottomTitle')}
                            >
                                <span className={`h-1.5 w-full rounded-full border shadow-sm ${sectionTint?.base || ''}`}></span>
                            </button>
                        )}
                    </div>
                );
            })}

            {activeResize && activeResizePreviewY !== null && (
                <div
                    className="pointer-events-none absolute left-5 right-5 z-30"
                    style={{ top: `${Math.max(activeResizePreviewY - 6, 0)}px` }}
                >
                    <div className={`h-3 rounded-full border shadow-lg ${getFieldTint(activeResize.code).base} ring-2 ${getFieldTint(activeResize.code).activePreviewRing}`}></div>
                </div>
            )}
        </div>
    );

    if (chromeless) {
        return canvas;
    }

    return (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
            <div className="relative border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-3 py-2">
                <div className="flex min-h-[40px] items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-medium text-gray-700">
                            {t('smartFieldOverlay.title')}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {t('smartFieldOverlay.description')}
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {selection && (
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                            >
                                {t('smartFieldOverlay.clearSelection')}
                            </button>
                        )}

                        <div ref={selectionMenuRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setIsSelectionMenuOpen((current) => !current)}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <span>{t('smartFieldOverlay.selectionCount', { total: totalSelections })}</span>
                                <svg className={`h-4 w-4 text-gray-400 transition-transform ${isSelectionMenuOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {isSelectionMenuOpen && (
                                <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                                    <div className="max-h-[320px] overflow-auto py-2">
                                        {assignmentSummaries.map((assignment) => {
                                            const isActive = Boolean(
                                                selection &&
                                                Math.min(selection.start, selection.end) === assignment.startLine &&
                                                Math.max(selection.start, selection.end) === assignment.endLine
                                            );

                                            return (
                                                <button
                                                    key={`${assignment.code}-${assignment.startLine}-${assignment.endLine}`}
                                                    type="button"
                                                    onClick={() => {
                                                        setIsSelectionMenuOpen(false);
                                                        focusAssignment(assignment);
                                                    }}
                                                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${isActive ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                                                >
                                                    <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full border ${getFieldTint(assignment.code).base}`}></span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm font-medium text-gray-900">{assignment.title}</span>
                                                        <span className="mt-0.5 block truncate text-xs text-gray-500">{assignment.preview}</span>
                                                    </span>
                                                    <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                                        {assignment.lineLabel}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {canvas}
        </div>
    );
};

export default SmartFieldOverlayCanvas;
